/**
 * Utilidades para parseo y manejo de direcciones
 * Centraliza la lógica de parsing de direcciones de inmuebles
 */

export interface ParsedAddress {
  tipoVia: string;
  numVia: string;
  currVia: string;
  compVia: string;
  apto: string;
  barrio: string;
}

/**
 * Parsea una dirección completa y extrae sus componentes
 *
 * Formatos soportados:
 * - "Cra 15 # 20 - 30"
 * - "Cra 15 # 20 - 30, Apto 501"
 * - "Cra 15 # 20 - 30, Apto 501, Centro"
 * - "Cra 15 # 20 - 30, Centro"
 * - "Dg 33 # 45 - 789, asd, asdasdasd" (primera parte después de coma es apto, segunda es barrio)
 *
 * @param address - Dirección completa a parsear
 * @returns Objeto con los componentes de la dirección
 */
export function parsePropertyAddress(address: string): ParsedAddress {
  const result: ParsedAddress = {
    tipoVia: '',
    numVia: '',
    currVia: '',
    compVia: '',
    apto: '',
    barrio: ''
  };

  if (!address) {
    console.warn('⚠️ PropertyAddress vacío');
    return result;
  }

  try {
    // Dividir por comas para separar dirección principal de apto/barrio
    const parts = address.split(',').map(p => p.trim());
    const mainAddress = parts[0]; // Ej: "Cra 15 # 20 - 30"

    // Parsear dirección principal
    // Formato esperado: "TipoVia NumVia # CurrVia - CompVia"
    const hashIndex = mainAddress.indexOf('#');
    const dashIndex = mainAddress.indexOf('-');

    if (hashIndex > 0) {
      // Parte antes del # (tipo de vía y número)
      const beforeHash = mainAddress.substring(0, hashIndex).trim();
      const beforeHashParts = beforeHash.split(' ');

      if (beforeHashParts.length >= 2) {
        result.tipoVia = beforeHashParts[0]; // Ej: "Cra"
        result.numVia = beforeHashParts.slice(1).join(' '); // Ej: "15" o "15 A"
      } else if (beforeHashParts.length === 1) {
        result.tipoVia = beforeHashParts[0];
      }

      // Parte después del # y antes del -
      if (dashIndex > hashIndex) {
        result.currVia = mainAddress.substring(hashIndex + 1, dashIndex).trim();
        result.compVia = mainAddress.substring(dashIndex + 1).trim();
      } else {
        // Solo hay # pero no -
        result.currVia = mainAddress.substring(hashIndex + 1).trim();
      }
    } else {
      // No tiene formato estándar, intentar dividir por espacios
      const addressParts = mainAddress.split(' ');
      if (addressParts.length >= 1) result.tipoVia = addressParts[0];
      if (addressParts.length >= 2) result.numVia = addressParts[1];
      if (addressParts.length >= 3) result.currVia = addressParts[2];
      if (addressParts.length >= 4) result.compVia = addressParts[3];
    }

    // Procesar partes adicionales (apto, barrio)
    // Lógica mejorada:
    // - Si hay exactamente 2 partes adicionales: primera=apto, segunda=barrio
    // - Si hay 1 parte con palabras clave de apto: apto
    // - Si hay 1 parte sin palabras clave: barrio
    // - Si hay más de 2 partes: detectar por palabras clave
    const additionalParts = parts.slice(1); // Todo después de la dirección principal

    if (additionalParts.length === 2) {
      // Dos partes: asumir primera=apto, segunda=barrio
      result.apto = additionalParts[0];
      result.barrio = additionalParts[1];
      console.log('📍 Detectado (2 partes) - Apto:', result.apto, 'Barrio:', result.barrio);
    } else if (additionalParts.length === 1) {
      const part = additionalParts[0].toLowerCase();
      // Si contiene palabras clave de apto o es solo números, es apto
      if (part.includes('apto') || part.includes('apt') || part.includes('apartamento') ||
          part.includes('interior') || part.includes('int') || /^\d+$/.test(additionalParts[0])) {
        result.apto = /^\d+$/.test(additionalParts[0]) ? `Apto ${additionalParts[0]}` : additionalParts[0];
        console.log('📍 Detectado (1 parte con keyword) - Apto:', result.apto);
      } else {
        // Si no tiene palabras clave, es barrio
        result.barrio = additionalParts[0];
        console.log('📍 Detectado (1 parte sin keyword) - Barrio:', result.barrio);
      }
    } else if (additionalParts.length > 2) {
      // Más de 2 partes: intentar detectar apto por palabras clave, resto es barrio
      for (let i = 0; i < additionalParts.length; i++) {
        const part = additionalParts[i].toLowerCase();
        if (!result.apto && (part.includes('apto') || part.includes('apt') || part.includes('apartamento') ||
            part.includes('interior') || part.includes('int') || /^\d+$/.test(additionalParts[i]))) {
          result.apto = /^\d+$/.test(additionalParts[i]) ? `Apto ${additionalParts[i]}` : additionalParts[i];
          console.log('📍 Detectado (>2 partes con keyword) - Apto:', result.apto);
        } else if (!result.barrio) {
          result.barrio = additionalParts[i];
          console.log('📍 Detectado (>2 partes resto) - Barrio:', result.barrio);
        }
      }
    }

  } catch (error) {
    console.error('❌ Error parseando dirección:', address, error);
  }

  console.log('📋 Resultado del parsing de dirección:', {
    original: address,
    parsed: result
  });

  return result;
}

/**
 * Construye una dirección completa a partir de sus componentes
 * @param parts - Componentes de la dirección
 * @returns Dirección formateada como string
 */
export function buildAddressString(parts: ParsedAddress): string {
  let address = `${parts.tipoVia} ${parts.numVia}`;

  if (parts.currVia) {
    address += ` # ${parts.currVia}`;
  }

  if (parts.compVia) {
    address += ` - ${parts.compVia}`;
  }

  if (parts.apto) {
    address += `, ${parts.apto}`;
  }

  if (parts.barrio) {
    address += `, ${parts.barrio}`;
  }

  return address.trim();
}
